// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Table, Avatar, Row, Col, Typography, Spin, Image } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { ArrowLeft, Edit, Clock, Download, FileText, Image as ImageIcon } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import type { Personnel } from '../types/personnel';
import { EditHistoryModal } from '../components/personnel/EditHistoryModal';
import { RejectConfirmModal } from '../components/personnel/RejectConfirmModal';
import { ApproveConfirmModal } from '../components/personnel/ApproveConfirmModal';
import { AdditionalPersonnelInfo } from '../components/personnel/AdditionalPersonnelInfo';
import type { PersonnelDetail } from '../types/personnelDetail';
import { formatRole } from '../utils/formatters';
import { usePersonnelDetail } from '@/features/personnel/usePersonnelDetail';
import { buildFullUrl } from '@/contexts/AvatarContext';
import styles from './PersonnelDetailPage.module.css';

const { Text, Title } = Typography;

export const PersonnelDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { personnel?: Personnel; refresh?: boolean } | null;
  const personnel = locationState?.personnel;

  const {
    detailData,
    loading,
    avatarUri,
    pendingUpdateRequest,
    currentValuesForReview,
    departmentNamesForReview,
    idCardsForReview,
    currentUserId,
    isHistoryModalOpen,
    setIsHistoryModalOpen,
    isRejectModalOpen,
    setIsRejectModalOpen,
    isApproveModalOpen,
    setIsApproveModalOpen,
    handleExport,
    handleRejectConfirm,
    handleApproveConfirm,
    renderValueWithComparison,
  } = usePersonnelDetail(personnel);

  // UI handlers - xử lý navigation và UI interactions
  const handleBack = () => {
    navigate(-1);
  };

  const handleUpdate = () => {
    if (!detailData) return;
    
    const personnelToUpdate: Personnel = personnel || {
      id: detailData.id,
      employeeCode: detailData.employeeCode,
      fullName: detailData.fullName,
      position: detailData.position || '',
      company: detailData.company || '',
      jobTitle: detailData.jobTitle || '',
      status: detailData.status,
      startDate: detailData.startDate,
      avatar: detailData.avatar,
      role: detailData.role,
      email: detailData.email,
    };
    navigate('/management/personnel/update-personal-info', {
      state: { personnel: personnelToUpdate },
    });
  };

  const handleHistory = () => {
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
  };

  const handleReject = () => {
    setIsRejectModalOpen(true);
  };

  const handleApprove = () => {
    setIsApproveModalOpen(true);
  };

  const currentJobColumns: ColumnsType<NonNullable<PersonnelDetail['currentJob']>> = [
    {
      title: 'CTY',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Phòng ban',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Chức vụ',
      dataIndex: 'position',
      key: 'position',
      render: (text: string) => formatRole(text),
    },
    {
      title: 'Vị trí',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: 'Mức lương',
      dataIndex: 'salary',
      key: 'salary',
    },
    {
      title: 'Ngày vào làm',
      dataIndex: 'startDate',
      key: 'startDate',
    },
  ];

  type TrainingPlace = NonNullable<PersonnelDetail['trainingPlaces']>[0];
  type WorkExperience = NonNullable<PersonnelDetail['workExperience']>[0];
  type FamilyMember = NonNullable<PersonnelDetail['familyMembers']>[0];
  type Certificate = NonNullable<PersonnelDetail['certificates']>[0];
  type PartyHistory = NonNullable<PersonnelDetail['partyHistory']>[0];

  const trainingColumns: ColumnsType<TrainingPlace> = [
    {
      title: 'Nơi đào tạo',
      dataIndex: 'place',
      key: 'place',
    },
    {
      title: 'Chuyên ngành',
      dataIndex: 'major',
      key: 'major',
    },
    {
      title: 'Hình thức đào tạo',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Trình độ',
      dataIndex: 'degree',
      key: 'degree',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
  ];

  const experienceColumns: ColumnsType<WorkExperience> = [
    {
      title: 'Tên CTY',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'Vị trí',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: 'Phòng ban',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Mức lương',
      dataIndex: 'salary',
      key: 'salary',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
  ];

  const familyColumns: ColumnsType<FamilyMember> = [
    {
      title: 'Phụ thuộc',
      dataIndex: 'relationship',
      key: 'relationship',
      render: (relationship: string) => relationship || '—',
    },
    {
      title: 'Họ và tên',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Năm sinh',
      dataIndex: 'birthYear',
      key: 'birthYear',
      render: (birthYear: number | null) => birthYear ?? '—',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Nghề nghiệp',
      dataIndex: 'occupation',
      key: 'occupation',
    },
    {
      title: 'Nơi làm việc',
      dataIndex: 'workplace',
      key: 'workplace',
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'address',
      key: 'address',
    },
  ];

  const certificateColumns: ColumnsType<Certificate> = [
    {
      title: 'Số hiệu',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Đơn vị ban hành',
      dataIndex: 'issuingBody',
      key: 'issuingBody',
    },
    {
      title: 'Loại chứng chỉ',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
    },
    {
      title: 'Ảnh chứng chỉ',
      dataIndex: 'attachments',
      key: 'attachments',
      render: (attachments: Certificate['attachments']) => attachments?.length
        ? <Image.PreviewGroup>{attachments.map((item) => <Image key={item.id} src={buildFullUrl(item.fileUrl) || undefined} alt={item.fileName} width={64} />)}</Image.PreviewGroup>
        : 'Chưa có ảnh',
    },
  ];

  const partyColumns: ColumnsType<PartyHistory> = [
    {
      title: 'Số thẻ',
      dataIndex: 'cardNumber',
      key: 'cardNumber',
    },
    {
      title: 'Hình thức',
      dataIndex: 'form',
      key: 'form',
    },
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: 'Nơi kết nạp',
      dataIndex: 'admissionPlace',
      key: 'admissionPlace',
    },
    {
      title: 'Nơi điều chuyển',
      dataIndex: 'transferPlace',
      key: 'transferPlace',
    },
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!detailData) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Text>Không tìm thấy thông tin nhân sự</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft} onClick={handleBack}>
          <ArrowLeft size={20} className={styles.backIcon} />
          <Text className={styles.breadcrumb}>Chi tiết nhân sự</Text>
        </div>
        <div className={styles.headerActions}>
          {pendingUpdateRequest ? (
            <>
              <Button
                type="default"
                onClick={handleReject}
                className={styles.rejectButton}
              >
                Từ chối
              </Button>
              <Button
                type="primary"
                onClick={handleApprove}
                className={styles.approveButton}
              >
                Xem trước và duyệt
              </Button>
            </>
          ) : (
            <>
              <Button
                type="default"
                icon={<Edit size={16} />}
                onClick={handleUpdate}
                className={styles.actionButton}
              >
                Cập nhật
              </Button>
              <Button
                type="default"
                icon={<Clock size={16} />}
                onClick={handleHistory}
                className={styles.actionButton}
              >
                Lịch sử cập nhật
              </Button>
              <Button
                type="default"
                icon={<Download size={16} />}
                onClick={handleExport}
                className={styles.actionButton}
              >
                Xuất file
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.sidebar}>
          <div className={styles.profileCard}>
            <Avatar 
              size={120} 
              src={avatarUri && avatarUri.trim() !== '' ? avatarUri : undefined}
              icon={!avatarUri || avatarUri.trim() === '' ? <UserOutlined /> : undefined}
              onError={() => false}
              className={styles.avatar} 
            />
            {(() => {
              const updateData = pendingUpdateRequest?.updateData || {};
              const newFullName = updateData.fullName;
              const hasChange = newFullName !== undefined && 
                                newFullName !== null && 
                                newFullName !== '' &&
                                String(newFullName).trim() !== String(detailData.fullName || '').trim();
              
              if (hasChange) {
                return (
                  <div className={styles.nameChangeContainer}>
                    <Title level={4} className={`${styles.profileName} ${styles.nameChangeNew}`}>
                      {String(newFullName)}
                    </Title>
                    <Text className={styles.nameChangeOld}>
                      {detailData.fullName || 'Chưa cập nhật tên'}
                    </Text>
                  </div>
                );
              }
              
              return (
                <Title level={4} className={styles.profileName}>
                  {detailData.fullName || 'Chưa cập nhật tên'}
                </Title>
              );
            })()}
            <div className={styles.roleTag}>
              {detailData.currentJob?.role || detailData.jobTitle || detailData.position || 'Chưa cập nhật vị trí'}
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <div className={styles.basicInfoList}>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Mã nhân sự:</Text>
                {renderValueWithComparison('employeeCode', detailData.employeeCode, 'employeeCode', styles.basicInfoValue)}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Email:</Text>
                {renderValueWithComparison('email', detailData.email, 'email', styles.basicInfoValue)}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Ngày sinh:</Text>
                {renderValueWithComparison('dateOfBirth', detailData.dateOfBirth, 'dateOfBirth', styles.basicInfoValue)}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Giới tính:</Text>
                {renderValueWithComparison('gender', detailData.gender, 'gender', styles.basicInfoValue)}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Số điện thoại:</Text>
                {renderValueWithComparison('phone', detailData.phone, 'phone', styles.basicInfoValue)}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Ngày gia nhập:</Text>
                {renderValueWithComparison('startDate', detailData.startDate, 'startDate', styles.basicInfoValue)}
              </div>
              <div className={styles.basicInfoItem}>
                <Text className={styles.basicInfoLabel}>Phòng ban:</Text>
                <Text className={styles.basicInfoValue}>
                  {detailData.currentJob?.department || 'Chưa cập nhật'}
                </Text>
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Thẻ định danh
            </Title>
            <div className={styles.imageGrid}>
              <div className={styles.imageItem}>
                <Text className={styles.imageLabel}>Ảnh CC/CCCD/CMT Mặt trước</Text>
                {detailData.idCardFront ? (
                  <Image
                    src={detailData.idCardFront}
                    alt="ID Card Front"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
              <div className={styles.imageItem}>
                <Text className={styles.imageLabel}>Ảnh CC/CCCD/CMT Mặt sau</Text>
                {detailData.idCardBack ? (
                  <Image
                    src={detailData.idCardBack}
                    alt="ID Card Back"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Ảnh hộ chiếu
            </Title>
            <div className={styles.imageGrid}>
              <div className={styles.imageItem}>
                {detailData.passportCover ? (
                  <Image
                    src={detailData.passportCover}
                    alt="Passport Cover"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
              <div className={styles.imageItem}>
                {detailData.passportPhoto ? (
                  <Image
                    src={detailData.passportPhoto}
                    alt="Passport Photo"
                    className={styles.idCardImage}
                    preview
                  />
                ) : (
                  <div className={styles.imagePlaceholder}>
                    <ImageIcon size={24} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              Lá số tử vi
            </Title>
            <div className={styles.imageItem}>
              {detailData.horoscope ? (
                <Image
                  src={detailData.horoscope}
                  alt="Horoscope"
                  className={styles.horoscopeImage}
                  preview
                />
              ) : (
                <div className={styles.largeImagePlaceholder}>
                  <ImageIcon size={32} />
                </div>
              )}
            </div>
          </div>

          <div className={styles.sidebarSection}>
            <Title level={5} className={styles.sidebarSectionTitle}>
              File lưu trữ
            </Title>
            <div className={styles.fileList}>
              {detailData.storedFiles && detailData.storedFiles.length > 0 ? (
                detailData.storedFiles.map((file, index) => (
                  <div key={index} className={styles.fileItem}>
                    <FileText size={16} className={styles.fileIcon} />
                    <Text className={styles.fileName}>{file.name || 'Chưa cập nhật tên file'}</Text>
                    {file.url ? (
                      <a
                        href={file.url}
                        download={file.name || 'download'}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Tải xuống ${file.name || 'file'}`}
                      >
                        <Download size={16} className={styles.downloadIcon} />
                      </a>
                    ) : (
                      <Download size={16} className={styles.downloadIcon} aria-disabled="true" />
                    )}
                  </div>
                ))
              ) : (
                <Text className={styles.emptyFileText}>Chưa cập nhật file nào</Text>
              )}
            </div>
          </div>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Giới thiệu bản thân
            </Title>
            {renderValueWithComparison('selfDescription', detailData.description, 'selfDescription', styles.description)}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin chi tiết
            </Title>
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Dân tộc:</Text>
                  {renderValueWithComparison('ethnicity', detailData.ethnicity, 'ethnicity', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Tôn giáo:</Text>
                  {renderValueWithComparison('religion', detailData.religion, 'religion', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Nơi sinh:</Text>
                  {renderValueWithComparison('placeOfBirth', detailData.placeOfBirth, 'placeOfBirth', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Quốc tịch:</Text>
                  {renderValueWithComparison('nationality', detailData.nationality, 'nationality', styles.infoValue)}
                </div>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Nguyên quán:</Text>
                  {renderValueWithComparison('hometown', 
                    detailData.hometown 
                      ? detailData.hometown.replace('Huyện ', '').replace('Tỉnh ', '').replace(', Việt Nam', '') 
                      : null
                  , 'hometown', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Thường trú:</Text>
                  {renderValueWithComparison('permanentAddress', detailData.permanentAddress, 'permanentAddress', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Địa chỉ:</Text>
                  {renderValueWithComparison('address', detailData.address, 'currentAddress', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Mã số thuế:</Text>
                  {renderValueWithComparison('personalTaxCode', detailData.taxCode, 'personalTaxCode', styles.infoValue)}
                </div>
              </Col>
            </Row>
          </div>

          <AdditionalPersonnelInfo data={detailData} />

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin ngân hàng
            </Title>
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Số Tài Khoản:</Text>
                  {renderValueWithComparison('bankAccount', detailData.bankAccount, 'bankAccount', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Tên người nhận:</Text>
                  {renderValueWithComparison('bankAccountHolder', detailData.bankAccountHolder, 'bankAccountHolder', styles.infoValue)}
                </div>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Ngân hàng:</Text>
                  {renderValueWithComparison('bankName', detailData.bankName, 'bankName', styles.infoValue)}
                </div>
                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Chi nhánh:</Text>
                  {renderValueWithComparison('bankBranch', detailData.bankBranch, 'bankBranch', styles.infoValue)}
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Công việc hiện nay
            </Title>
            {detailData.currentJob && (
              <Table
                columns={currentJobColumns}
                dataSource={[detailData.currentJob]}
                pagination={false}
                rowKey="company"
                className={styles.table}
                scroll={{ x: 'max-content' }}
              />
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Học vấn & Kinh nghiệm
            </Title>
            {detailData.education && (
              <div className={styles.educationInfo}>
                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Trình độ học vấn cao nhất:</Text>
                      {renderValueWithComparison('generalEducationLevel', detailData.education.highestLevel, 'generalEducationLevel', styles.infoValue)}
                    </div>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Trình độ phổ thông:</Text>
                      {renderValueWithComparison(
                        'generalEducationLevel',
                        detailData.education.generalLevel,
                        'generalEducationLevel',
                        styles.infoValue
                      )}
                    </div>
                  </Col>
                </Row>
                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Chuyên ngành:</Text>
                      {renderValueWithComparison('specialization', detailData.education.major, 'specialization', styles.infoValue)}
                    </div>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <div className={styles.infoRow}>
                      <Text className={styles.infoLabel}>Số năm kinh nghiệm trước đây:</Text>
                      {renderValueWithComparison('yearsOfExperience', 
                        detailData.education.yearsOfExperience !== undefined && detailData.education.yearsOfExperience !== null
                          ? detailData.education.yearsOfExperience
                          : null,
                        'yearsOfExperience',
                        styles.infoValue
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            
            <div className={styles.subsection}>
              {detailData.trainingPlaces && (
                <Table
                  columns={trainingColumns}
                  dataSource={detailData.trainingPlaces}
                  pagination={false}
                  rowKey="place"
                  className={styles.table}
                  scroll={{ x: 'max-content' }}
                />
              )}
            </div>

            <div className={styles.subsection}>
              {detailData.workExperience && (
                <Table
                  columns={experienceColumns}
                  dataSource={detailData.workExperience}
                  pagination={false}
                  rowKey="company"
                  className={styles.table}
                  scroll={{ x: 'max-content' }}
                />
              )}
            </div>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Thông tin gia đình & người phụ thuộc
            </Title>
            {detailData.familyMembers && (
              <Table
                columns={familyColumns}
                dataSource={detailData.familyMembers}
                pagination={false}
                rowKey="name"
                className={styles.table}
                scroll={{ x: 'max-content' }}
              />
            )}
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Chứng chỉ
            </Title>
            {detailData.certificates && (
              <Table
                columns={certificateColumns}
                dataSource={detailData.certificates}
                pagination={false}
                rowKey="id"
                className={styles.table}
                scroll={{ x: 'max-content' }}
              />
            )}

            <div style={{ marginTop: 16 }}>
              <Text className={styles.infoLabel}>Kỹ năng:</Text>
              <Text className={styles.infoValue} style={{ marginLeft: 8 }}>
                {detailData.skills && detailData.skills.length > 0
                  ? detailData.skills.join(', ')
                  : 'Chưa cập nhật'}
              </Text>
            </div>
          </div>

          <div className={styles.section}>
            <Title level={4} className={styles.sectionTitle}>
              Lịch sử đảng viên
            </Title>
            {detailData.partyHistory && (
              <Table
                columns={partyColumns}
                dataSource={detailData.partyHistory}
                pagination={false}
                rowKey="cardNumber"
                className={styles.table}
                scroll={{ x: 'max-content' }}
              />
            )}
          </div>
        </div>
      </div>

      <EditHistoryModal
        open={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        userId={currentUserId}
      />
      
      <RejectConfirmModal
        open={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={handleRejectConfirm}
      />
      
      <ApproveConfirmModal
        open={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onConfirm={handleApproveConfirm}
        request={pendingUpdateRequest}
        currentData={detailData}
        currentValues={currentValuesForReview}
        departmentNames={departmentNamesForReview}
        idCards={idCardsForReview}
      />
    </div>
  );
};
